// ═══════════════════════════════════════════════════════════════
// OmniPACS & RIS — App.jsx v3.0
// Sistema completo con todos los módulos
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from "react";

const API = "https://api.tomografiascusco.com";

// ─── HELPERS ─────────────────────────────────────────────────
const calcularEdad = (fechaNac) => {
  if (!fechaNac) return "";
  const hoy = new Date();
  const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
};

const fmt = (n) => `S/ ${parseFloat(n || 0).toFixed(2)}`;
const hoy = () => new Date().toISOString().slice(0, 10);

// ─── API CALLS ────────────────────────────────────────────────
const api = async (method, path, body, token) => {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Error del servidor");
  return data;
};

// ─── COLORES UI ───────────────────────────────────────────────
const COLORS = {
  primary: "#1a56db",
  success: "#057a55",
  danger: "#e02424",
  warning: "#c27803",
  info: "#1c64f2",
  gray: "#6b7280",
  bg: "#f9fafb",
  white: "#ffffff",
  border: "#e5e7eb",
  text: "#111827",
  muted: "#6b7280",
};

// ─── COMPONENTES BASE ─────────────────────────────────────────

const Badge = ({ color = "gray", children }) => {
  const map = {
    green: { bg: "#def7ec", c: "#057a55" },
    red: { bg: "#fde8e8", c: "#e02424" },
    yellow: { bg: "#fdf6b2", c: "#8e4b10" },
    blue: { bg: "#e1effe", c: "#1a56db" },
    gray: { bg: "#f3f4f6", c: "#374151" },
    purple: { bg: "#edebfe", c: "#5521b5" },
  };
  const s = map[color] || map.gray;
  return (
    <span style={{
      background: s.bg, color: s.c, fontSize: 11, fontWeight: 600,
      padding: "2px 8px", borderRadius: 4, whiteSpace: "nowrap"
    }}>{children}</span>
  );
};

const Btn = ({ onClick, color = "primary", children, size = "md", disabled }) => {
  const bg = {
    primary: "#1a56db", danger: "#e02424", success: "#057a55",
    gray: "#6b7280", warning: "#c27803", outline: "transparent"
  };
  const pad = size === "sm" ? "4px 10px" : size === "lg" ? "10px 22px" : "7px 16px";
  return (
    <button disabled={disabled} onClick={onClick} style={{
      background: color === "outline" ? "transparent" : bg[color] || bg.primary,
      color: color === "outline" ? "#374151" : "#fff",
      border: color === "outline" ? "1px solid #d1d5db" : "none",
      borderRadius: 6, padding: pad, fontSize: size === "sm" ? 12 : 14,
      fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1, transition: "opacity .15s"
    }}>{children}</button>
  );
};

const Input = ({ label, value, onChange, type = "text", required, placeholder, readOnly, style }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
      {label}{required && <span style={{ color: "#e02424" }}> *</span>}
    </label>}
    <input type={type} value={value || ""} onChange={e => onChange && onChange(e.target.value)}
      placeholder={placeholder} readOnly={readOnly}
      style={{
        width: "100%", padding: "7px 10px", borderRadius: 6,
        border: "1px solid #d1d5db", fontSize: 14, background: readOnly ? "#f9fafb" : "#fff",
        boxSizing: "border-box", ...style
      }} />
  </div>
);

const Select = ({ label, value, onChange, options = [], required, placeholder }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>
      {label}{required && <span style={{ color: "#e02424" }}> *</span>}
    </label>}
    <select value={value || ""} onChange={e => onChange(e.target.value)}
      style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, background: "#fff", boxSizing: "border-box" }}>
      <option value="">{placeholder || "— Seleccionar —"}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const Textarea = ({ label, value, onChange, rows = 3 }) => (
  <div style={{ marginBottom: 12 }}>
    {label && <label style={{ fontSize: 13, fontWeight: 500, color: "#374151", display: "block", marginBottom: 4 }}>{label}</label>}
    <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={rows}
      style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box", resize: "vertical" }} />
  </div>
);

const Modal = ({ title, onClose, children, width = 560 }) => (
  <div style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16
  }}>
    <div style={{
      background: "#fff", borderRadius: 10, width: "100%", maxWidth: width,
      maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.2)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
        <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>×</button>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  </div>
);

const Table = ({ cols, data, actions }) => (
  <div style={{ overflowX: "auto" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ background: "#f3f4f6" }}>
          {cols.map(c => <th key={c.key} style={{ padding: "9px 12px", textAlign: "left", fontWeight: 600, color: "#374151", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{c.label}</th>)}
          {actions && <th style={{ padding: "9px 12px" }}>Acciones</th>}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 && <tr><td colSpan={cols.length + (actions ? 1 : 0)} style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>Sin registros</td></tr>}
        {data.map((row, i) => (
          <tr key={row.id || i} style={{ borderBottom: "1px solid #f3f4f6" }}
            onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
            onMouseLeave={e => e.currentTarget.style.background = ""}>
            {cols.map(c => (
              <td key={c.key} style={{ padding: "9px 12px", color: "#374151", ...c.style }}>
                {c.render ? c.render(row[c.key], row) : row[c.key] ?? "—"}
              </td>
            ))}
            {actions && <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>{actions(row)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Alert = ({ type = "info", children }) => {
  const map = { error: ["#fde8e8", "#e02424"], success: ["#def7ec", "#057a55"], info: ["#e1effe", "#1a56db"], warning: ["#fdf6b2", "#92400e"] };
  const [bg, c] = map[type] || map.info;
  return <div style={{ background: bg, color: c, padding: "10px 14px", borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{children}</div>;
};

// ═══════════════════════════════════════════════════════════════
// MÓDULO LOGIN
// ═══════════════════════════════════════════════════════════════
function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError(""); setLoading(true);
    try {
      const data = await api("POST", "/auth/login", { email, password });
      onLogin(data.token, data.user);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#1a56db 0%,#1e429f 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 12, padding: 36, width: 360, boxShadow: "0 20px 60px rgba(0,0,0,.2)" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 4 }}>🏥</div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" }}>OmniPACS</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>Centro de Tomografía Cusco</p>
        </div>
        {error && <Alert type="error">{error}</Alert>}
        <Input label="Correo electrónico" value={email} onChange={setEmail} type="email" required />
        <Input label="Contraseña" value={password} onChange={setPassword} type="password" required />
        <Btn onClick={handleLogin} color="primary" size="lg" disabled={loading}>
          {loading ? "Ingresando..." : "Ingresar"}
        </Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO TIPOS DE ESTUDIO
// ═══════════════════════════════════════════════════════════════
function TiposEstudio({ token }) {
  const [data, setData] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    const r = await api("GET", "/tipos-estudio", null, token);
    setData(r);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      if (form.id) await api("PUT", `/tipos-estudio/${form.id}`, form, token);
      else await api("POST", "/tipos-estudio", form, token);
      setOk("Guardado correctamente"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  const openNew = () => { setForm({ incluye_informe: true, incluye_cd: false, requiere_contraste: false, duracion_minutos: 30 }); setModal("form"); setError(""); };
  const openEdit = (r) => { setForm(r); setModal("form"); setError(""); };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Tipos de Estudio</h2>
        <Btn onClick={openNew}>+ Nuevo tipo</Btn>
      </div>
      {ok && <Alert type="success">{ok}</Alert>}
      <Table
        cols={[
          { key: "codigo", label: "Código" },
          { key: "nombre", label: "Nombre" },
          { key: "precio_base", label: "Precio base", render: v => fmt(v) },
          { key: "precio_con_contraste", label: "Con contraste", render: v => v ? fmt(v) : "—" },
          { key: "incluye_informe", label: "Informe", render: v => <Badge color={v ? "green" : "gray"}>{v ? "Sí" : "No"}</Badge> },
          { key: "incluye_cd", label: "CD", render: v => <Badge color={v ? "blue" : "gray"}>{v ? "Sí" : "No"}</Badge> },
          { key: "duracion_minutos", label: "Duración", render: v => `${v} min` },
          { key: "activo", label: "Estado", render: v => <Badge color={v ? "green" : "red"}>{v ? "Activo" : "Inactivo"}</Badge> },
        ]}
        data={data}
        actions={r => <Btn size="sm" color="outline" onClick={() => openEdit(r)}>Editar</Btn>}
      />
      {modal === "form" && (
        <Modal title={form.id ? "Editar tipo de estudio" : "Nuevo tipo de estudio"} onClose={() => setModal(null)}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Código" value={form.codigo} onChange={v => setF("codigo", v)} required />
            <Input label="Duración (min)" type="number" value={form.duracion_minutos} onChange={v => setF("duracion_minutos", v)} />
          </div>
          <Input label="Nombre del estudio" value={form.nombre} onChange={v => setF("nombre", v)} required />
          <Textarea label="Descripción" value={form.descripcion} onChange={v => setF("descripcion", v)} rows={2} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Precio base (S/)" type="number" value={form.precio_base} onChange={v => setF("precio_base", v)} required />
            <Input label="Precio con contraste (S/)" type="number" value={form.precio_con_contraste} onChange={v => setF("precio_con_contraste", v)} />
          </div>
          <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
            {[["incluye_informe", "Incluye informe médico"], ["incluye_cd", "Incluye CD/archivo"], ["requiere_contraste", "Requiere contraste"]].map(([k, l]) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form[k]} onChange={e => setF(k, e.target.checked)} />
                {l}
              </label>
            ))}
          </div>
          {form.id && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 12 }}>
              <input type="checkbox" checked={!!form.activo} onChange={e => setF("activo", e.target.checked)} />
              Activo
            </label>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Guardar</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO MÉDICOS REFERENTES
// ═══════════════════════════════════════════════════════════════
function MedicosReferentes({ token }) {
  const [data, setData] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    const [meds, esps] = await Promise.all([
      api("GET", `/medicos-referentes?buscar=${buscar}`, null, token),
      api("GET", "/especialidades", null, token),
    ]);
    setData(meds); setEspecialidades(esps);
  }, [token, buscar]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      if (form.id) await api("PUT", `/medicos-referentes/${form.id}`, form, token);
      else await api("POST", "/medicos-referentes", form, token);
      setOk("Guardado"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  const openNew = () => { setForm({ comision_porcentaje: 0, activo: true }); setModal("form"); setError(""); };
  const openEdit = (r) => { setForm(r); setModal("form"); setError(""); };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Médicos Referentes</h2>
        <Btn onClick={openNew}>+ Nuevo médico</Btn>
      </div>
      <div style={{ marginBottom: 16 }}>
        <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar por nombre, CMP, especialidad..."
          style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db", width: 320, fontSize: 14 }} />
      </div>
      {ok && <Alert type="success">{ok}</Alert>}
      <Table
        cols={[
          { key: "apellidos", label: "Apellidos", render: (v, r) => `${v}, ${r.nombres}` },
          { key: "cmp", label: "CMP" },
          { key: "especialidad_nombre", label: "Especialidad" },
          { key: "celular", label: "Celular" },
          { key: "institucion", label: "Institución", style: { maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" } },
          { key: "visitador_nombres", label: "Visitador asignado", render: (v, r) => v ? `${r.visitador_apellidos}, ${v}` : <Badge color="gray">Sin asignar</Badge> },
          { key: "comision_porcentaje", label: "Comisión", render: v => `${v}%` },
          { key: "activo", label: "Estado", render: v => <Badge color={v ? "green" : "red"}>{v ? "Activo" : "Inactivo"}</Badge> },
        ]}
        data={data}
        actions={r => <Btn size="sm" color="outline" onClick={() => openEdit(r)}>Editar</Btn>}
      />
      {modal === "form" && (
        <Modal title={form.id ? "Editar médico" : "Nuevo médico referente"} onClose={() => setModal(null)} width={680}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombres" value={form.nombres} onChange={v => setF("nombres", v)} required />
            <Input label="Apellidos" value={form.apellidos} onChange={v => setF("apellidos", v)} required />
            <Input label="DNI" value={form.dni} onChange={v => setF("dni", v)} />
            <Input label="CMP" value={form.cmp} onChange={v => setF("cmp", v)} placeholder="Número CMP" />
          </div>
          <Select label="Especialidad" value={form.especialidad_id}
            onChange={v => setF("especialidad_id", v)}
            options={especialidades.map(e => ({ value: e.id, label: e.nombre }))} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Teléfono" value={form.telefono} onChange={v => setF("telefono", v)} />
            <Input label="Celular" value={form.celular} onChange={v => setF("celular", v)} />
          </div>
          <Input label="Email" value={form.email} onChange={v => setF("email", v)} type="email" />
          <Input label="Institución / Hospital / Clínica" value={form.institucion} onChange={v => setF("institucion", v)} />
          <Textarea label="Dirección consultorio" value={form.direccion_consultorio} onChange={v => setF("direccion_consultorio", v)} rows={2} />
          <Input label="Referencia de dirección" value={form.referencia_direccion} onChange={v => setF("referencia_direccion", v)} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Comisión (%)" type="number" value={form.comision_porcentaje} onChange={v => setF("comision_porcentaje", v)} />
            {form.id && (
              <div style={{ paddingTop: 24 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!form.activo} onChange={e => setF("activo", e.target.checked)} />
                  Médico activo
                </label>
              </div>
            )}
          </div>
          <Textarea label="Notas" value={form.notas} onChange={v => setF("notas", v)} rows={2} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Guardar</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO VISITADORES
// ═══════════════════════════════════════════════════════════════
function Visitadores({ token }) {
  const [visitadores, setVisitadores] = useState([]);
  const [medicosLibres, setMedicosLibres] = useState([]);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [medicosList, setMedicosList] = useState([]);
  const [form, setForm] = useState({});
  const [medicoAsignar, setMedicoAsignar] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    const r = await api("GET", "/visitadores", null, token);
    setVisitadores(r);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      if (form.id) await api("PUT", `/visitadores/${form.id}`, form, token);
      else await api("POST", "/visitadores", form, token);
      setOk("Guardado"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  const abrirMedicos = async (v) => {
    setSelected(v);
    const [lista, libres] = await Promise.all([
      api("GET", `/visitadores/${v.id}/medicos`, null, token),
      api("GET", "/medicos-referentes?sin_visitador=true", null, token),
    ]);
    setMedicosList(lista);
    setMedicosLibres(libres);
    setMedicoAsignar("");
    setModal("medicos");
  };

  const asignar = async () => {
    if (!medicoAsignar) return;
    setError("");
    try {
      await api("POST", `/visitadores/${selected.id}/asignar-medico`, { medico_ref_id: parseInt(medicoAsignar) }, token);
      setOk("Médico asignado");
      abrirMedicos(selected);
    } catch (e) { setError(e.message); }
  };

  const desasignar = async (medicoId) => {
    try {
      await api("DELETE", `/visitadores/${selected.id}/medico/${medicoId}`, null, token);
      abrirMedicos(selected);
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Visitadores Médicos</h2>
        <Btn onClick={() => { setForm({ comision_porcentaje: 0 }); setModal("form"); setError(""); }}>+ Nuevo visitador</Btn>
      </div>
      {ok && <Alert type="success">{ok}</Alert>}
      <Table
        cols={[
          { key: "apellidos", label: "Apellidos", render: (v, r) => `${v}, ${r.nombres}` },
          { key: "dni", label: "DNI" },
          { key: "celular", label: "Celular" },
          { key: "zona", label: "Zona" },
          { key: "total_medicos", label: "Médicos asignados", render: v => <Badge color="blue">{v} médicos</Badge> },
          { key: "comision_porcentaje", label: "Comisión", render: v => `${v}%` },
          { key: "activo", label: "Estado", render: v => <Badge color={v ? "green" : "red"}>{v ? "Activo" : "Inactivo"}</Badge> },
        ]}
        data={visitadores}
        actions={r => (
          <div style={{ display: "flex", gap: 6 }}>
            <Btn size="sm" color="outline" onClick={() => { setForm(r); setModal("form"); setError(""); }}>Editar</Btn>
            <Btn size="sm" color="primary" onClick={() => abrirMedicos(r)}>Ver médicos</Btn>
          </div>
        )}
      />

      {modal === "form" && (
        <Modal title={form.id ? "Editar visitador" : "Nuevo visitador"} onClose={() => setModal(null)}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombres" value={form.nombres} onChange={v => setF("nombres", v)} required />
            <Input label="Apellidos" value={form.apellidos} onChange={v => setF("apellidos", v)} required />
            <Input label="DNI" value={form.dni} onChange={v => setF("dni", v)} />
            <Input label="Celular" value={form.celular} onChange={v => setF("celular", v)} />
          </div>
          <Input label="Email" value={form.email} onChange={v => setF("email", v)} type="email" />
          <Input label="Zona de trabajo" value={form.zona} onChange={v => setF("zona", v)} />
          <Input label="Comisión (%)" type="number" value={form.comision_porcentaje} onChange={v => setF("comision_porcentaje", v)} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Guardar</Btn>
          </div>
        </Modal>
      )}

      {modal === "medicos" && selected && (
        <Modal title={`Médicos de: ${selected.nombres} ${selected.apellidos}`} onClose={() => setModal(null)} width={680}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 600, color: "#166534" }}>Asignar médico sin visitador:</p>
            <div style={{ display: "flex", gap: 8 }}>
              <select value={medicoAsignar} onChange={e => setMedicoAsignar(e.target.value)}
                style={{ flex: 1, padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}>
                <option value="">— Seleccionar médico disponible —</option>
                {medicosLibres.map(m => (
                  <option key={m.id} value={m.id}>{m.apellidos}, {m.nombres} — {m.especialidad_nombre || "Sin esp."} — {m.institucion || ""}</option>
                ))}
              </select>
              <Btn onClick={asignar} color="success">Asignar</Btn>
            </div>
            {medicosLibres.length === 0 && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7280" }}>Todos los médicos tienen visitador asignado</p>}
          </div>
          <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Médicos asignados ({medicosList.length})</h4>
          <Table
            cols={[
              { key: "apellidos", label: "Médico", render: (v, r) => `${v}, ${r.nombres}` },
              { key: "especialidad_nombre", label: "Especialidad" },
              { key: "celular", label: "Celular" },
              { key: "institucion", label: "Institución" },
            ]}
            data={medicosList}
            actions={r => <Btn size="sm" color="danger" onClick={() => desasignar(r.id)}>Quitar</Btn>}
          />
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO JALADORES
// ═══════════════════════════════════════════════════════════════
function Jaladores({ token }) {
  const [data, setData] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    const r = await api("GET", "/jaladores", null, token);
    setData(r);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      if (form.id) await api("PUT", `/jaladores/${form.id}`, form, token);
      else await api("POST", "/jaladores", form, token);
      setOk("Guardado"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Jaladores / Personal de Derivación</h2>
        <Btn onClick={() => { setForm({ comision_monto: 0, comision_porcentaje: 0 }); setModal("form"); setError(""); }}>+ Nuevo jalador</Btn>
      </div>
      {ok && <Alert type="success">{ok}</Alert>}
      <Table
        cols={[
          { key: "apellidos", label: "Nombre", render: (v, r) => `${v}, ${r.nombres}` },
          { key: "dni", label: "DNI" },
          { key: "celular", label: "Celular" },
          { key: "zona", label: "Zona" },
          { key: "comision_monto", label: "Comisión fija", render: v => v > 0 ? fmt(v) : "—" },
          { key: "comision_porcentaje", label: "Comisión %", render: v => v > 0 ? `${v}%` : "—" },
          { key: "activo", label: "Estado", render: v => <Badge color={v ? "green" : "red"}>{v ? "Activo" : "Inactivo"}</Badge> },
        ]}
        data={data}
        actions={r => <Btn size="sm" color="outline" onClick={() => { setForm(r); setModal("form"); setError(""); }}>Editar</Btn>}
      />
      {modal === "form" && (
        <Modal title={form.id ? "Editar jalador" : "Nuevo jalador"} onClose={() => setModal(null)}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombres" value={form.nombres} onChange={v => setF("nombres", v)} required />
            <Input label="Apellidos" value={form.apellidos} onChange={v => setF("apellidos", v)} required />
            <Input label="DNI" value={form.dni} onChange={v => setF("dni", v)} />
            <Input label="Celular" value={form.celular} onChange={v => setF("celular", v)} />
          </div>
          <Input label="Zona de trabajo" value={form.zona} onChange={v => setF("zona", v)} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Comisión fija (S/)" type="number" value={form.comision_monto} onChange={v => setF("comision_monto", v)} placeholder="0.00" />
            <Input label="Comisión (%)" type="number" value={form.comision_porcentaje} onChange={v => setF("comision_porcentaje", v)} placeholder="0.00" />
          </div>
          <Textarea label="Notas" value={form.notas} onChange={v => setF("notas", v)} rows={2} />
          {form.id && <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 12 }}>
            <input type="checkbox" checked={!!form.activo} onChange={e => setF("activo", e.target.checked)} /> Activo
          </label>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Guardar</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO USUARIOS
// ═══════════════════════════════════════════════════════════════
function Usuarios({ token }) {
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    const [u, r] = await Promise.all([api("GET", "/usuarios", null, token), api("GET", "/roles", null, token)]);
    setData(u); setRoles(r);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      if (form.id) await api("PUT", `/usuarios/${form.id}`, form, token);
      else await api("POST", "/usuarios", form, token);
      setOk("Guardado"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Usuarios del Sistema</h2>
        <Btn onClick={() => { setForm({ activo: true, rol_id: 2 }); setModal("form"); setError(""); }}>+ Nuevo usuario</Btn>
      </div>
      {ok && <Alert type="success">{ok}</Alert>}
      <Table
        cols={[
          { key: "apellidos", label: "Nombre", render: (v, r) => `${v}, ${r.nombres}` },
          { key: "email", label: "Email" },
          { key: "rol", label: "Rol", render: v => <Badge color="purple">{v}</Badge> },
          { key: "ultimo_login", label: "Último login", render: v => v ? new Date(v).toLocaleString("es-PE") : "Nunca" },
          { key: "activo", label: "Estado", render: v => <Badge color={v ? "green" : "red"}>{v ? "Activo" : "Inactivo"}</Badge> },
        ]}
        data={data}
        actions={r => <Btn size="sm" color="outline" onClick={() => { setForm({ ...r, password: "" }); setModal("form"); setError(""); }}>Editar</Btn>}
      />
      {modal === "form" && (
        <Modal title={form.id ? "Editar usuario" : "Nuevo usuario"} onClose={() => setModal(null)}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Nombres" value={form.nombres} onChange={v => setF("nombres", v)} required />
            <Input label="Apellidos" value={form.apellidos} onChange={v => setF("apellidos", v)} required />
          </div>
          <Input label="Email" value={form.email} onChange={v => setF("email", v)} type="email" required />
          <Input label={form.id ? "Nueva contraseña (vacío = no cambiar)" : "Contraseña"} value={form.password} onChange={v => setF("password", v)} type="password" required={!form.id} />
          <Select label="Rol" value={form.rol_id} onChange={v => setF("rol_id", parseInt(v))} required
            options={roles.map(r => ({ value: r.id, label: r.nombre }))} />
          {form.id && <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 12 }}>
            <input type="checkbox" checked={!!form.activo} onChange={e => setF("activo", e.target.checked)} /> Activo
          </label>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Guardar</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO INSUMOS
// ═══════════════════════════════════════════════════════════════
function Insumos({ token }) {
  const [data, setData] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [movForm, setMovForm] = useState({ tipo: "ENTRADA", cantidad: "", motivo: "" });
  const [selectedInsumo, setSelectedInsumo] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    const r = await api("GET", "/insumos", null, token);
    setData(r);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    try {
      if (form.id) await api("PUT", `/insumos/${form.id}`, form, token);
      else await api("POST", "/insumos", form, token);
      setOk("Guardado"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  const registrarMov = async () => {
    setError("");
    try {
      await api("POST", `/insumos/${selectedInsumo.id}/movimiento`, movForm, token);
      setOk("Movimiento registrado"); setModal(null); load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Insumos / Inventario</h2>
        <Btn onClick={() => { setForm({ stock_actual: 0, stock_minimo: 0, precio_unitario: 0 }); setModal("form"); setError(""); }}>+ Nuevo insumo</Btn>
      </div>
      {ok && <Alert type="success">{ok}</Alert>}
      <Table
        cols={[
          { key: "codigo", label: "Código" },
          { key: "nombre", label: "Nombre" },
          { key: "unidad_medida", label: "Unidad" },
          { key: "stock_actual", label: "Stock actual", render: (v, r) => (
            <span style={{ color: parseFloat(v) <= parseFloat(r.stock_minimo) ? "#e02424" : "#057a55", fontWeight: 600 }}>
              {parseFloat(v).toFixed(2)} {r.unidad_medida}
            </span>
          )},
          { key: "stock_minimo", label: "Stock mínimo" },
          { key: "precio_unitario", label: "Precio unit.", render: v => fmt(v) },
          { key: "proveedor", label: "Proveedor" },
        ]}
        data={data}
        actions={r => (
          <div style={{ display: "flex", gap: 6 }}>
            <Btn size="sm" color="outline" onClick={() => { setForm(r); setModal("form"); setError(""); }}>Editar</Btn>
            <Btn size="sm" color="success" onClick={() => { setSelectedInsumo(r); setMovForm({ tipo: "ENTRADA", cantidad: "", motivo: "" }); setModal("mov"); setError(""); }}>Movimiento</Btn>
          </div>
        )}
      />
      {modal === "form" && (
        <Modal title={form.id ? "Editar insumo" : "Nuevo insumo"} onClose={() => setModal(null)}>
          {error && <Alert type="error">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Input label="Código" value={form.codigo} onChange={v => setF("codigo", v)} />
            <Input label="Unidad de medida" value={form.unidad_medida} onChange={v => setF("unidad_medida", v)} placeholder="unidad, ml, mg..." />
          </div>
          <Input label="Nombre del insumo" value={form.nombre} onChange={v => setF("nombre", v)} required />
          <Textarea label="Descripción" value={form.descripcion} onChange={v => setF("descripcion", v)} rows={2} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 12px" }}>
            {!form.id && <Input label="Stock inicial" type="number" value={form.stock_actual} onChange={v => setF("stock_actual", v)} />}
            <Input label="Stock mínimo (alerta)" type="number" value={form.stock_minimo} onChange={v => setF("stock_minimo", v)} />
            <Input label="Precio unitario (S/)" type="number" value={form.precio_unitario} onChange={v => setF("precio_unitario", v)} />
          </div>
          <Input label="Proveedor" value={form.proveedor} onChange={v => setF("proveedor", v)} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Guardar</Btn>
          </div>
        </Modal>
      )}
      {modal === "mov" && selectedInsumo && (
        <Modal title={`Movimiento: ${selectedInsumo.nombre}`} onClose={() => setModal(null)} width={400}>
          {error && <Alert type="error">{error}</Alert>}
          <p style={{ margin: "0 0 12px", fontSize: 14 }}>Stock actual: <strong>{parseFloat(selectedInsumo.stock_actual).toFixed(2)} {selectedInsumo.unidad_medida}</strong></p>
          <Select label="Tipo" value={movForm.tipo} onChange={v => setMovForm(f => ({ ...f, tipo: v }))}
            options={[{ value: "ENTRADA", label: "Entrada (compra/recepción)" }, { value: "SALIDA", label: "Salida (uso/descarte)" }, { value: "AJUSTE", label: "Ajuste (corrección)" }]} />
          <Input label="Cantidad" type="number" value={movForm.cantidad} onChange={v => setMovForm(f => ({ ...f, cantidad: v }))} required />
          <Textarea label="Motivo / Observación" value={movForm.motivo} onChange={v => setMovForm(f => ({ ...f, motivo: v }))} rows={2} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn color="outline" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={registrarMov}>Registrar</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO RECEPCIÓN — REGISTRAR ESTUDIO
// ═══════════════════════════════════════════════════════════════
function Recepcion({ token, user }) {
  const [estudios, setEstudios] = useState([]);
  const [modal, setModal] = useState(null);
  const [paso, setPaso] = useState(1);
  const [pacBuscar, setPacBuscar] = useState("");
  const [pacResultados, setPacResultados] = useState([]);
  const [pacSeleccionado, setPacSeleccionado] = useState(null);
  const [pacForm, setPacForm] = useState({});
  const [tiposEstudio, setTiposEstudio] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [jaladores, setJaladores] = useState([]);
  const [estudForm, setEstudForm] = useState({});
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [fechaFiltro, setFechaFiltro] = useState(hoy());

  const loadEstudios = useCallback(async () => {
    const r = await api("GET", `/estudios?fecha=${fechaFiltro}`, null, token);
    setEstudios(r);
  }, [token, fechaFiltro]);

  useEffect(() => { loadEstudios(); }, [loadEstudios]);

  const buscarPaciente = async () => {
    if (pacBuscar.length < 3) return;
    const r = await api("GET", `/pacientes?buscar=${pacBuscar}`, null, token);
    setPacResultados(r);
  };

  const seleccionarPaciente = (p) => {
    setPacSeleccionado(p);
    setPacResultados([]);
    setPacBuscar(`${p.apellido_paterno} ${p.nombres}`);
  };

  const abrirNuevoEstudio = async () => {
    const [tipos, meds, jals] = await Promise.all([
      api("GET", "/tipos-estudio", null, token),
      api("GET", "/medicos-referentes", null, token),
      api("GET", "/jaladores", null, token),
    ]);
    setTiposEstudio(tipos); setMedicos(meds); setJaladores(jals);
    setPacSeleccionado(null); setPacBuscar(""); setPacResultados([]);
    setPacForm({ tipo_documento: "DNI", sexo: "M" });
    setEstudForm({
      fecha_programada: hoy(), metodo_pago: "EFECTIVO",
      tipo_comprobante: "BOLETA", incluye_informe: true, incluye_cd: false, con_contraste: false
    });
    setPaso(1); setError(""); setModal("nuevo");
  };

  const setPF = (k, v) => setPacForm(f => ({ ...f, [k]: v }));
  const setEF = (k, v) => setEstudForm(f => ({ ...f, [k]: v }));

  const onTipoEstudioChange = (id) => {
    setEF("tipo_estudio_id", id);
    const t = tiposEstudio.find(t => t.id === parseInt(id));
    if (t) {
      setEF("precio_base", t.precio_base);
      setEF("precio_final", t.precio_base);
      setEF("incluye_informe", !!t.incluye_informe);
      setEF("incluye_cd", !!t.incluye_cd);
    }
  };

  const onContrasteChange = (v) => {
    setEF("con_contraste", v);
    const t = tiposEstudio.find(t => t.id === parseInt(estudForm.tipo_estudio_id));
    if (t && v && t.precio_con_contraste) {
      setEF("precio_final", t.precio_con_contraste);
      setEF("precio_base", t.precio_con_contraste);
    } else if (t) {
      setEF("precio_final", t.precio_base);
      setEF("precio_base", t.precio_base);
    }
  };

  const guardarPacienteNuevo = async () => {
    setError("");
    try {
      const r = await api("POST", "/pacientes", pacForm, token);
      setPacSeleccionado({ id: r.id, ...pacForm });
      setPaso(2);
    } catch (e) { setError(e.message); }
  };

  const guardarEstudio = async () => {
    setError("");
    try {
      const body = { ...estudForm, paciente_id: pacSeleccionado.id };
      const r = await api("POST", "/estudios", body, token);
      setOk(`Estudio registrado: ${r.comprobante}`);
      setModal(null); loadEstudios();
    } catch (e) { setError(e.message); }
  };

  const estadoColor = { PROGRAMADO: "blue", EN_PROCESO: "yellow", REALIZADO: "green", INFORMADO: "purple", ENTREGADO: "green", CANCELADO: "red" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Recepción</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="date" value={fechaFiltro} onChange={e => setFechaFiltro(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 }} />
          <Btn onClick={abrirNuevoEstudio}>+ Nuevo estudio</Btn>
        </div>
      </div>
      {ok && <Alert type="success">{ok}</Alert>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
        {["PROGRAMADO", "EN_PROCESO", "REALIZADO", "ENTREGADO"].map(estado => (
          <div key={estado} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px" }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>
              {estudios.filter(e => e.estado === estado).length}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{estado.replace("_", " ")}</div>
          </div>
        ))}
      </div>

      <Table
        cols={[
          { key: "paciente_nombre", label: "Paciente", style: { maxWidth: 180 } },
          { key: "edad_calculada", label: "Edad", render: v => v ? `${v} años` : "—" },
          { key: "tipo_estudio_nombre", label: "Estudio" },
          { key: "hora_programada", label: "Hora", render: v => v?.slice(0, 5) || "—" },
          { key: "medico_nombre", label: "Médico ref." },
          { key: "con_contraste", label: "Contraste", render: v => v ? <Badge color="yellow">Con contraste</Badge> : "—" },
          { key: "precio_final", label: "Precio", render: v => fmt(v) },
          { key: "tipo_comprobante", label: "Comprobante", render: (v, r) => r.numero_comprobante_completo || v },
          { key: "metodo_pago", label: "Pago" },
          { key: "estado", label: "Estado", render: v => <Badge color={estadoColor[v] || "gray"}>{v}</Badge> },
        ]}
        data={estudios}
        actions={r => (
          <select value={r.estado} onChange={async e => {
            await api("PUT", `/estudios/${r.id}/estado`, { estado: e.target.value }, token);
            loadEstudios();
          }} style={{ fontSize: 12, padding: "3px 6px", borderRadius: 4, border: "1px solid #d1d5db" }}>
            {["PROGRAMADO", "EN_PROCESO", "REALIZADO", "INFORMADO", "ENTREGADO", "CANCELADO"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
      />

      {modal === "nuevo" && (
        <Modal title="Nuevo estudio" onClose={() => setModal(null)} width={720}>
          {error && <Alert type="error">{error}</Alert>}

          {/* PASOS */}
          <div style={{ display: "flex", gap: 0, marginBottom: 20, borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
            {[["1", "Paciente"], ["2", "Estudio"], ["3", "Pago"]].map(([n, l]) => (
              <div key={n} style={{
                flex: 1, padding: "10px 0", textAlign: "center", fontSize: 13, fontWeight: parseInt(n) === paso ? 600 : 400,
                background: parseInt(n) === paso ? "#1a56db" : parseInt(n) < paso ? "#def7ec" : "#f9fafb",
                color: parseInt(n) === paso ? "#fff" : parseInt(n) < paso ? "#057a55" : "#6b7280",
                cursor: parseInt(n) < paso ? "pointer" : "default"
              }} onClick={() => parseInt(n) < paso && setPaso(parseInt(n))}>
                {n}. {l}
              </div>
            ))}
          </div>

          {/* PASO 1: PACIENTE */}
          {paso === 1 && (
            <div>
              <h4 style={{ margin: "0 0 12px" }}>Buscar paciente existente</h4>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input value={pacBuscar} onChange={e => setPacBuscar(e.target.value)} placeholder="DNI, nombre o celular..."
                  style={{ flex: 1, padding: "8px 12px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 }} />
                <Btn onClick={buscarPaciente}>Buscar</Btn>
              </div>
              {pacResultados.length > 0 && (
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
                  {pacResultados.map(p => (
                    <div key={p.id} onClick={() => seleccionarPaciente(p)}
                      style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f3f4f6", fontSize: 14 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <strong>{p.apellido_paterno} {p.apellido_materno}, {p.nombres}</strong>
                      <span style={{ color: "#6b7280", marginLeft: 8 }}>{p.tipo_documento}: {p.numero_documento} | {p.edad_calculada} años | {p.celular}</span>
                    </div>
                  ))}
                </div>
              )}
              {pacSeleccionado && (
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <p style={{ margin: 0, fontWeight: 600, color: "#166534" }}>✓ Paciente seleccionado</p>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#374151" }}>
                    {pacSeleccionado.apellido_paterno} {pacSeleccionado.apellido_materno}, {pacSeleccionado.nombres} |
                    {" "}{pacSeleccionado.tipo_documento}: {pacSeleccionado.numero_documento} |
                    {" "}Edad: {calcularEdad(pacSeleccionado.fecha_nacimiento)} años |
                    {" "}Cel: {pacSeleccionado.celular}
                  </p>
                </div>
              )}

              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", fontSize: 14, color: "#1a56db", fontWeight: 500 }}>
                  + Registrar paciente nuevo
                </summary>
                <div style={{ marginTop: 12, padding: 12, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                    <Select label="Tipo doc." value={pacForm.tipo_documento} onChange={v => setPF("tipo_documento", v)}
                      options={[{value:"DNI",label:"DNI"},{value:"CE",label:"CE"},{value:"PASAPORTE",label:"Pasaporte"}]} />
                    <Input label="Nº Documento" value={pacForm.numero_documento} onChange={v => setPF("numero_documento", v)} required />
                    <Input label="Apellido Paterno" value={pacForm.apellido_paterno} onChange={v => setPF("apellido_paterno", v)} required />
                    <Input label="Apellido Materno" value={pacForm.apellido_materno} onChange={v => setPF("apellido_materno", v)} />
                    <Input label="Nombres" value={pacForm.nombres} onChange={v => setPF("nombres", v)} required style={{ gridColumn: "span 2" }} />
                    <div>
                      <Input label="Fecha de nacimiento" type="date" value={pacForm.fecha_nacimiento}
                        onChange={v => { setPF("fecha_nacimiento", v); }} required />
                      {pacForm.fecha_nacimiento && (
                        <p style={{ margin: "-8px 0 8px", fontSize: 13, color: "#1a56db", fontWeight: 600 }}>
                          Edad: {calcularEdad(pacForm.fecha_nacimiento)} años
                        </p>
                      )}
                    </div>
                    <Select label="Sexo" value={pacForm.sexo} onChange={v => setPF("sexo", v)}
                      options={[{value:"M",label:"Masculino"},{value:"F",label:"Femenino"}]} required />
                    <Input label="Celular" value={pacForm.celular} onChange={v => setPF("celular", v)} required />
                    <Input label="Email (opcional)" value={pacForm.email} onChange={v => setPF("email", v)} type="email" />
                  </div>
                  <Textarea label="Alergias conocidas (especialmente al contraste)" value={pacForm.alergias} onChange={v => setPF("alergias", v)} rows={2} />
                  <Btn onClick={guardarPacienteNuevo} color="success">Registrar y continuar</Btn>
                </div>
              </details>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <Btn onClick={() => setPaso(2)} disabled={!pacSeleccionado}>Siguiente →</Btn>
              </div>
            </div>
          )}

          {/* PASO 2: ESTUDIO */}
          {paso === 2 && (
            <div>
              <Select label="Tipo de estudio" value={estudForm.tipo_estudio_id} onChange={onTipoEstudioChange} required
                options={tiposEstudio.map(t => ({ value: t.id, label: `${t.nombre} — ${fmt(t.precio_base)}` }))} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <Input label="Fecha programada" type="date" value={estudForm.fecha_programada} onChange={v => setEF("fecha_programada", v)} required />
                <Input label="Hora programada" type="time" value={estudForm.hora_programada} onChange={v => setEF("hora_programada", v)} />
              </div>
              <Select label="Médico que envía (si es referente)" value={estudForm.medico_referente_id}
                onChange={v => setEF("medico_referente_id", v)}
                options={medicos.map(m => ({ value: m.id, label: `${m.apellidos}, ${m.nombres} — ${m.especialidad_nombre || ""}` }))}
                placeholder="— Sin médico referente —" />
              <Select label="Jalador que refirió al paciente" value={estudForm.jalador_id}
                onChange={v => setEF("jalador_id", v)}
                options={jaladores.map(j => ({ value: j.id, label: `${j.apellidos}, ${j.nombres}` }))}
                placeholder="— Sin jalador —" />

              <div style={{ display: "flex", gap: 24, marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!estudForm.con_contraste} onChange={e => onContrasteChange(e.target.checked)} />
                  Con medio de contraste
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!estudForm.incluye_informe} onChange={e => setEF("incluye_informe", e.target.checked)} />
                  Incluye informe médico
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                  <input type="checkbox" checked={!!estudForm.incluye_cd} onChange={e => setEF("incluye_cd", e.target.checked)} />
                  Incluye CD
                </label>
              </div>

              {estudForm.con_contraste && (
                <Input label="Tipo de contraste" value={estudForm.tipo_contraste} onChange={v => setEF("tipo_contraste", v)} placeholder="Ej: Iohexol 350mg" />
              )}

              <Textarea label="Diagnóstico presuntivo / Indicación clínica" value={estudForm.diagnostico_presuntivo} onChange={v => setEF("diagnostico_presuntivo", v)} rows={2} />
              <Textarea label="Síntomas del paciente" value={estudForm.sintomas} onChange={v => setEF("sintomas", v)} rows={2} />

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                <Btn color="outline" onClick={() => setPaso(1)}>← Atrás</Btn>
                <Btn onClick={() => setPaso(3)} disabled={!estudForm.tipo_estudio_id}>Siguiente →</Btn>
              </div>
            </div>
          )}

          {/* PASO 3: PAGO Y COMPROBANTE */}
          {paso === 3 && (
            <div>
              <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 13, color: "#0369a1" }}>
                  <strong>Paciente:</strong> {pacSeleccionado?.apellido_paterno} {pacSeleccionado?.nombres} |
                  <strong> Estudio:</strong> {tiposEstudio.find(t => t.id === parseInt(estudForm.tipo_estudio_id))?.nombre}
                </p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <Input label="Precio base (S/)" type="number" value={estudForm.precio_base} onChange={v => setEF("precio_base", v)} readOnly />
                <Input label="Precio final (S/)" type="number" value={estudForm.precio_final} onChange={v => setEF("precio_final", v)} required />
                <Input label="Descuento (S/)" type="number" value={estudForm.descuento_monto} onChange={v => { setEF("descuento_monto", v); setEF("precio_final", (parseFloat(estudForm.precio_base) - parseFloat(v || 0)).toFixed(2)); }} />
                <Input label="Motivo del descuento" value={estudForm.descuento_motivo} onChange={v => setEF("descuento_motivo", v)} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <Select label="Método de pago" value={estudForm.metodo_pago} onChange={v => setEF("metodo_pago", v)} required
                  options={[{value:"EFECTIVO",label:"Efectivo"},{value:"YAPE",label:"Yape"},{value:"PLIN",label:"Plin"},{value:"TRANSFERENCIA",label:"Transferencia"},{value:"TARJETA",label:"Tarjeta"}]} />
                <Select label="Tipo de comprobante" value={estudForm.tipo_comprobante} onChange={v => setEF("tipo_comprobante", v)} required
                  options={[{value:"BOLETA",label:"Boleta de venta"},{value:"FACTURA",label:"Factura"},{value:"NOTA_VENTA",label:"Nota de venta"},{value:"RECIBO",label:"Recibo"}]} />
              </div>

              {estudForm.tipo_comprobante === "FACTURA" && (
                <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: 13 }}>Datos para la factura:</p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0 12px" }}>
                    <Input label="RUC" value={estudForm.ruc_factura} onChange={v => setEF("ruc_factura", v)} placeholder="20123456789" />
                    <Input label="Razón Social" value={estudForm.razon_social_factura} onChange={v => setEF("razon_social_factura", v)} />
                  </div>
                </div>
              )}

              <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 14, marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span>Precio base:</span><span>{fmt(estudForm.precio_base)}</span>
                </div>
                {parseFloat(estudForm.descuento_monto) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: "#e02424" }}>
                    <span>Descuento:</span><span>- {fmt(estudForm.descuento_monto)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 700, borderTop: "1px solid #e5e7eb", marginTop: 8, paddingTop: 8 }}>
                  <span>TOTAL A COBRAR:</span><span style={{ color: "#057a55" }}>{fmt(estudForm.precio_final)}</span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Btn color="outline" onClick={() => setPaso(2)}>← Atrás</Btn>
                <Btn onClick={guardarEstudio} color="success" size="lg">✓ Registrar y emitir comprobante</Btn>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MÓDULO REPORTES
// ═══════════════════════════════════════════════════════════════
function Reportes({ token }) {
  const [reporte, setReporte] = useState("caja");
  const [fecha, setFecha] = useState(hoy());
  const [fechaI, setFechaI] = useState(hoy());
  const [fechaF, setFechaF] = useState(hoy());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      let url = "";
      if (reporte === "caja") url = `/reportes/caja?fecha=${fecha}`;
      else if (reporte === "medicos") url = `/reportes/produccion-medicos?fecha_inicio=${fechaI}&fecha_fin=${fechaF}`;
      else if (reporte === "jaladores") url = `/reportes/produccion-jaladores?fecha_inicio=${fechaI}&fecha_fin=${fechaF}`;
      else if (reporte === "tipos") url = `/reportes/estudios-por-tipo?fecha_inicio=${fechaI}&fecha_fin=${fechaF}`;
      else if (reporte === "contraste") url = `/reportes/contraste?fecha_inicio=${fechaI}&fecha_fin=${fechaF}`;
      else if (reporte === "comprobantes") url = `/reportes/comprobantes?fecha_inicio=${fechaI}&fecha_fin=${fechaF}`;
      const r = await api("GET", url, null, token);
      setData(r);
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 600 }}>Reportes</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {[
          ["caja", "Caja del día"],
          ["medicos", "Producción médicos"],
          ["jaladores", "Producción jaladores"],
          ["tipos", "Por tipo de estudio"],
          ["contraste", "Pacientes con contraste"],
          ["comprobantes", "Comprobantes"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => { setReporte(k); setData(null); }}
            style={{
              padding: "6px 14px", borderRadius: 6, border: "1px solid",
              borderColor: reporte === k ? "#1a56db" : "#d1d5db",
              background: reporte === k ? "#1a56db" : "#fff",
              color: reporte === k ? "#fff" : "#374151",
              fontSize: 13, cursor: "pointer", fontWeight: reporte === k ? 600 : 400
            }}>{l}</button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
        {reporte === "caja" ? (
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 }} />
        ) : (
          <>
            <input type="date" value={fechaI} onChange={e => setFechaI(e.target.value)}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 }} />
            <span>hasta</span>
            <input type="date" value={fechaF} onChange={e => setFechaF(e.target.value)}
              style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 14 }} />
          </>
        )}
        <Btn onClick={cargar} disabled={loading}>{loading ? "Cargando..." : "Generar reporte"}</Btn>
      </div>

      {data && reporte === "caja" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1 }}>Total estudios</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{data.totales?.total_estudios || 0}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1 }}>Total del día</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#057a55" }}>{fmt(data.totales?.total_dia)}</div>
            </div>
            <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1 }}>Total descuentos</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: "#e02424" }}>{fmt(data.totales?.total_descuentos)}</div>
            </div>
          </div>
          <Table
            cols={[
              { key: "metodo_pago", label: "Método de pago" },
              { key: "tipo_comprobante", label: "Comprobante" },
              { key: "cantidad", label: "Cantidad" },
              { key: "total", label: "Total", render: v => fmt(v) },
            ]}
            data={data.desglose || []} />
        </div>
      )}

      {data && reporte === "medicos" && Array.isArray(data) && (
        <Table
          cols={[
            { key: "medico", label: "Médico" },
            { key: "especialidad", label: "Especialidad" },
            { key: "total_estudios", label: "Estudios" },
            { key: "total_facturado", label: "Facturado", render: v => fmt(v) },
            { key: "comision_porcentaje", label: "Comisión %", render: v => `${v}%` },
            { key: "comision_calculada", label: "Comisión S/", render: v => <strong style={{ color: "#057a55" }}>{fmt(v)}</strong> },
          ]}
          data={data} />
      )}

      {data && reporte === "jaladores" && Array.isArray(data) && (
        <Table
          cols={[
            { key: "jalador", label: "Jalador" },
            { key: "total_pacientes", label: "Pacientes" },
            { key: "total_facturado", label: "Facturado", render: v => fmt(v) },
            { key: "comision_calculada", label: "Comisión S/", render: v => <strong style={{ color: "#057a55" }}>{fmt(v)}</strong> },
          ]}
          data={data} />
      )}

      {data && reporte === "tipos" && Array.isArray(data) && (
        <Table
          cols={[
            { key: "codigo", label: "Código" },
            { key: "nombre", label: "Tipo de estudio" },
            { key: "cantidad", label: "Cantidad" },
            { key: "total", label: "Total", render: v => fmt(v) },
            { key: "promedio", label: "Precio promedio", render: v => fmt(v) },
          ]}
          data={data} />
      )}

      {data && reporte === "contraste" && Array.isArray(data) && (
        <div>
          <Alert type="warning">Reporte de {data.length} pacientes con contraste en el período</Alert>
          <Table
            cols={[
              { key: "paciente", label: "Paciente" },
              { key: "fecha_nacimiento", label: "F. Nacimiento" },
              { key: "edad_calculada", label: "Edad", render: v => `${v} años` },
              { key: "sexo", label: "Sexo" },
              { key: "paciente_celular", label: "Celular" },
              { key: "tipo_estudio", label: "Estudio" },
              { key: "tipo_contraste", label: "Contraste" },
              { key: "diagnostico_presuntivo", label: "Diagnóstico" },
              { key: "sintomas", label: "Síntomas" },
              { key: "alergias", label: "Alergias", render: v => v ? <Badge color="red">{v}</Badge> : "—" },
              { key: "fecha_programada", label: "Fecha" },
            ]}
            data={data} />
        </div>
      )}

      {data && reporte === "comprobantes" && Array.isArray(data) && (
        <Table
          cols={[
            { key: "numero_comprobante_completo", label: "Comprobante" },
            { key: "tipo_comprobante", label: "Tipo" },
            { key: "fecha_programada", label: "Fecha" },
            { key: "paciente", label: "Paciente" },
            { key: "ruc_factura", label: "RUC" },
            { key: "precio_final", label: "Importe", render: v => fmt(v) },
            { key: "descuento_monto", label: "Descuento", render: v => parseFloat(v) > 0 ? fmt(v) : "—" },
            { key: "metodo_pago", label: "Pago" },
          ]}
          data={data} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// APP PRINCIPAL
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("omnipacs_token"));
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("omnipacs_user")); } catch { return null; }
  });
  const [seccion, setSeccion] = useState("recepcion");

  const handleLogin = (t, u) => {
    setToken(t); setUser(u);
    localStorage.setItem("omnipacs_token", t);
    localStorage.setItem("omnipacs_user", JSON.stringify(u));
  };

  const logout = () => {
    setToken(null); setUser(null);
    localStorage.removeItem("omnipacs_token");
    localStorage.removeItem("omnipacs_user");
  };

  if (!token) return <Login onLogin={handleLogin} />;

  const esAdmin = user?.rol === "admin";

  const MENU = [
    { id: "recepcion", label: "🏥 Recepción", always: true },
    { id: "tipos-estudio", label: "🔬 Tipos de estudio", admin: true },
    { id: "medicos", label: "👨‍⚕️ Médicos referentes", admin: true },
    { id: "visitadores", label: "🚗 Visitadores", admin: true },
    { id: "jaladores", label: "🤝 Jaladores", admin: true },
    { id: "usuarios", label: "👤 Usuarios", admin: true },
    { id: "insumos", label: "📦 Insumos", always: true },
    { id: "reportes", label: "📊 Reportes", always: true },
  ].filter(m => m.always || (m.admin && esAdmin));

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "system-ui, sans-serif" }}>
      {/* SIDEBAR */}
      <div style={{ width: 220, background: "#1e293b", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "20px 16px 16px", borderBottom: "1px solid #334155" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>🏥 OmniPACS</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>Centro Tomografía Cusco</div>
        </div>
        <nav style={{ flex: 1, padding: "8px 0", overflowY: "auto" }}>
          {MENU.map(m => (
            <button key={m.id} onClick={() => setSeccion(m.id)} style={{
              display: "block", width: "100%", textAlign: "left",
              padding: "9px 16px", fontSize: 13, border: "none", cursor: "pointer",
              background: seccion === m.id ? "#1a56db" : "transparent",
              color: seccion === m.id ? "#fff" : "#94a3b8",
              fontWeight: seccion === m.id ? 600 : 400,
              borderRadius: 0, transition: "all .1s"
            }}>{m.label}</button>
          ))}
        </nav>
        <div style={{ padding: 12, borderTop: "1px solid #334155" }}>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
            {user?.nombres} {user?.apellidos}<br />
            <span style={{ color: "#64748b" }}>{user?.rol}</span>
          </div>
          <Btn onClick={logout} color="outline" size="sm">Salir</Btn>
        </div>
      </div>

      {/* CONTENIDO */}
      <div style={{ flex: 1, background: "#f8fafc", overflowY: "auto" }}>
        <div style={{ padding: 24, maxWidth: 1200 }}>
          {seccion === "recepcion" && <Recepcion token={token} user={user} />}
          {seccion === "tipos-estudio" && <TiposEstudio token={token} />}
          {seccion === "medicos" && <MedicosReferentes token={token} />}
          {seccion === "visitadores" && <Visitadores token={token} />}
          {seccion === "jaladores" && <Jaladores token={token} />}
          {seccion === "usuarios" && <Usuarios token={token} />}
          {seccion === "insumos" && <Insumos token={token} />}
          {seccion === "reportes" && <Reportes token={token} />}
        </div>
      </div>
    </div>
  );
}